import { Star } from "lucide-react";
import { nextEstimateDay, slotsLeftText, useNextEstimate, usePublicSite } from "@/lib/publicSite";
import { COPY } from "@/constants/copy";

// Hero card: the next real open estimate window, and what the $50 covers.
export const EstimateSlotCard = ({ onBook, testid = "hero-availability" }) => {
  const next = useNextEstimate();
  const day = nextEstimateDay(next);
  const left = slotsLeftText(next);
  return (
    <>
      <p className="overline-label">Daily estimate meetings</p>
      <p className="font-display font-bold text-navy text-lg mt-1" data-testid={`${testid}-next`}>
        {day ? (
          <button
            type="button"
            data-testid={`${testid}-pick-day`}
            data-book-cta
            onClick={() => onBook(null, "estimate", { date: next.date })}
            className="text-left py-2 -my-2 hover:text-terracotta transition-colors"
            aria-label={`Book the ${day} estimate visit`}
          >
            Next estimate: <span className="underline decoration-dotted decoration-terracotta underline-offset-4">{day}</span> · <span className="text-terracotta" data-testid={`${testid}-window`}>4–5 PM</span>
          </button>
        ) : (
          <>Weekdays · <span className="text-terracotta" data-testid={`${testid}-window`}>4–5 PM</span></>
        )}
      </p>
      {left && (
        <p className="text-xs font-semibold text-terracotta mt-0.5 flex items-center gap-1.5" data-testid={`${testid}-left`}>
          <span className="h-1.5 w-1.5 rounded-full bg-terracotta animate-pulse" aria-hidden /> {left}
        </p>
      )}
      <p className="text-xs text-muted-foreground mt-0.5 max-w-[17rem]" data-testid={`${testid}-credit`}>{COPY.credit}</p>
      <p className="text-xs text-muted-foreground mt-0.5 max-w-[17rem]" data-testid={`${testid}-rates`}>{COPY.rates}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <button
          data-testid={`${testid}-book`}
          data-book-cta
          onClick={() => onBook(null)}
          className="py-2 -my-2 text-sm font-semibold text-terracotta hover:text-terracotta-hover transition-colors"
        >
          Book the $50 visit →
        </button>
        <button
          type="button"
          data-testid={`${testid}-custom-time`}
          data-book-cta
          onClick={() => onBook(null, "estimate", { custom: true })}
          className="py-2 -my-2 text-xs font-semibold text-navy underline decoration-dotted underline-offset-4 hover:text-terracotta transition-colors"
        >
          Need another time?
        </button>
      </div>
    </>
  );
};

// "5.0 ★ on Google · 12 reviews" — shows once the owner has entered the rating in the admin.
export const GoogleRatingBadge = ({ testid = "hero-google-rating", compact = false }) => {
  const google = usePublicSite()?.google;
  if (!google?.rating) return null;
  const count = google.count ? ` · ${google.count} review${google.count === 1 ? "" : "s"}` : "";
  return (
    <a href={google.url} target="_blank" rel="noreferrer" data-testid={testid}
      className={`flex items-center gap-3 group ${compact ? "text-sm font-semibold text-navy" : ""}`}>
      <Star className={`${compact ? "h-4 w-4" : "h-5 w-5"} fill-terracotta text-terracotta`} />
      {compact ? (
        <span className="group-hover:text-terracotta transition-colors">{Number(google.rating).toFixed(1)} ★ on Google{count}</span>
      ) : (
        <div>
          <p className="font-display font-bold text-navy leading-none group-hover:text-terracotta transition-colors">
            {Number(google.rating).toFixed(1)} ★ on Google{count}
          </p>
          <p className="text-xs text-muted-foreground mt-1 underline decoration-dotted underline-offset-4">read the reviews</p>
        </div>
      )}
    </a>
  );
};
